/**
 * Validation of the channel settings (core/model.ts `ChannelSettings`): the same rules apply when a
 * document is loaded, when the UI edits a field and before anything is sent to the control plane.
 */
import {
  ACCENT_COLOR_PATTERN,
  CHANNEL_LINK_TITLE_MAX,
  CHANNEL_LINKS_MAX,
  CHANNEL_URL_MAX,
  CONTENT_LABELS,
  DEFAULT_ACCENT_COLOR,
  DEFAULT_PORTAL_URL,
  STREAM_LANGUAGE_PATTERN,
  STREAM_TAG_PATTERN,
  STREAM_TAGS_MAX,
  CHANNEL_SLUG_PATTERN,
  SCHEDULE_DURATION_MAX,
  SCHEDULE_DURATION_MIN,
  SCHEDULE_ENTRIES_MAX,
  STREAM_CATEGORY_MAX,
  STREAM_TITLE_MAX,
  SUGGESTED_CHANNELS_MAX,
  GOAL_TARGET_MAX,
  GOAL_TITLE_MAX,
  CLIP_SECONDS,
  CATEGORY_SLUG_PATTERN,
  CLIP_EXCLUDED_CATEGORIES_MAX,
  CLIP_REACTION_EMOJIS_MAX,
  DEFAULT_CLIP_REACTION_EMOJIS,
  FOLLOW_AGE_MINUTES,
  VOD_RETENTION_DAYS_MAX,
  FAVOURITE_NAME_MAX,
  FAVOURITE_NOTE_MAX,
  FAVOURITES_MAX,
  type ChannelFavourite,
  type ChannelGoal,
  type ChannelLink,
  type ClipSettings,
  type ClipSeconds,
  type RaidSettings,
  type ContentLabel,
  type FeaturedContent,
  type ScheduleEntry,
} from './model';

/** `https://` only, so the portal can render it as a link or an <img>; '' otherwise. */
export function httpsUrl(v: unknown, max = CHANNEL_URL_MAX): string {
  if (typeof v !== 'string') return '';
  const text = v.trim();
  if (text.length > max) return '';
  try {
    return new URL(text).protocol === 'https:' ? text : '';
  } catch {
    return '';
  }
}

/** Drops control and bidi characters (they hide text or reverse it), trims, and cuts to `max`. */
export function cleanText(v: unknown, max: number): string {
  if (typeof v !== 'string') return '';
  // eslint-disable-next-line no-control-regex
  return v.replace(/[\u0000-\u001f\u007f-\u009f\u200e\u200f\u202a-\u202e\u2066-\u2069]/g, '').trim().slice(0, max);
}

/** Like cleanText but keeps line breaks (normalised to `\n`), for the bio. */
export function cleanMultilineText(v: unknown, max: number): string {
  if (typeof v !== 'string') return '';
  return v
    .replace(/\r\n?/g, '\n')
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0009\u000b-\u001f\u007f-\u009f\u200e\u200f\u202a-\u202e\u2066-\u2069]/g, '')
    .trim()
    .slice(0, max);
}

/** Valid, case-insensitively unique tags in their first spelling, at most STREAM_TAGS_MAX. */
export function cleanTags(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const tag of v) {
    if (typeof tag !== 'string') continue;
    const text = tag.trim();
    if (!STREAM_TAG_PATTERN.test(text) || seen.has(text.toLowerCase())) continue;
    seen.add(text.toLowerCase());
    out.push(text);
    if (out.length >= STREAM_TAGS_MAX) break;
  }
  return out;
}

export function cleanLanguage(v: unknown): string {
  return typeof v === 'string' && STREAM_LANGUAGE_PATTERN.test(v.trim()) ? v.trim() : '';
}

export function cleanAccentColor(v: unknown): string {
  return typeof v === 'string' && ACCENT_COLOR_PATTERN.test(v.trim()) ? v.trim().toLowerCase() : DEFAULT_ACCENT_COLOR;
}

export function cleanLinks(v: unknown): ChannelLink[] {
  if (!Array.isArray(v)) return [];
  const out: ChannelLink[] = [];
  for (const raw of v) {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) continue;
    const link = raw as Record<string, unknown>;
    const url = httpsUrl(link.url);
    if (!url) continue;
    out.push({ title: cleanText(link.title, CHANNEL_LINK_TITLE_MAX), url });
    if (out.length >= CHANNEL_LINKS_MAX) break;
  }
  return out;
}

export function cleanContentLabels(v: unknown): ContentLabel[] {
  if (!Array.isArray(v)) return [];
  return CONTENT_LABELS.filter((label) => v.includes(label));
}

/** An https origin such as https://ompeer.com (http only to loopback), without a path; the default otherwise. */
export function cleanPortalUrl(v: unknown): string {
  if (typeof v !== 'string') return DEFAULT_PORTAL_URL;
  try {
    const url = new URL(v.trim());
    const loopback = ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname) || url.hostname.endsWith('.localhost');
    if (url.protocol !== 'https:' && !(url.protocol === 'http:' && loopback)) return DEFAULT_PORTAL_URL;
    return url.origin;
  } catch {
    return DEFAULT_PORTAL_URL;
  }
}


const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** The browser's zone, or UTC where Intl cannot tell. */
export function localTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** True for an IANA zone name this runtime can format dates in. */
export function isTimeZone(v: unknown): v is string {
  if (typeof v !== 'string' || v.length === 0 || v.length > 64) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: v });
    return true;
  } catch {
    return false;
  }
}

function intIn(v: unknown, min: number, max: number): number | null {
  if (typeof v !== 'number' || !Number.isFinite(v)) return null;
  const n = Math.trunc(v);
  return n < min || n > max ? null : n;
}

/** Unique ascending weekdays 0–6; [] when nothing valid is left. */
export function cleanDays(v: unknown): number[] {
  if (!Array.isArray(v)) return [];
  const days = new Set<number>();
  for (const day of v) {
    const n = intIn(day, 0, 6);
    if (n !== null) days.add(n);
  }
  return [...days].sort((a, b) => a - b);
}

/** One schedule entry, or null when its time, duration, days or zone are unusable. */
export function cleanScheduleEntry(v: unknown): ScheduleEntry | null {
  if (!isRecord(v)) return null;
  const startMinutes = intIn(v.startMinutes, 0, 1439);
  const durationMinutes = intIn(v.durationMinutes, SCHEDULE_DURATION_MIN, SCHEDULE_DURATION_MAX);
  const days = cleanDays(v.days);
  if (startMinutes === null || durationMinutes === null || days.length === 0 || !isTimeZone(v.timeZone)) return null;
  return {
    title: cleanText(v.title, STREAM_TITLE_MAX),
    category: cleanText(v.category, STREAM_CATEGORY_MAX),
    startMinutes,
    durationMinutes,
    days,
    timeZone: v.timeZone,
  };
}

export function cleanSchedule(v: unknown): ScheduleEntry[] {
  if (!Array.isArray(v)) return [];
  const out: ScheduleEntry[] = [];
  for (const raw of v) {
    const entry = cleanScheduleEntry(raw);
    if (entry) out.push(entry);
    if (out.length >= SCHEDULE_ENTRIES_MAX) break;
  }
  return out;
}

/** Valid, unique channel slugs, at most SUGGESTED_CHANNELS_MAX. */
export function cleanSlugs(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  const out: string[] = [];
  for (const raw of v) {
    if (typeof raw !== 'string') continue;
    const slug = raw.trim().toLowerCase();
    if (!CHANNEL_SLUG_PATTERN.test(slug) || out.includes(slug)) continue;
    out.push(slug);
    if (out.length >= SUGGESTED_CHANNELS_MAX) break;
  }
  return out;
}

export function defaultFeatured(): FeaturedContent {
  return { suggestedChannels: [], suggestedOrder: 'listed', showRecentCategories: true, panel: 'suggested' };
}

export function cleanFeatured(v: unknown): FeaturedContent {
  const f = isRecord(v) ? v : {};
  return {
    suggestedChannels: cleanSlugs(f.suggestedChannels),
    suggestedOrder: f.suggestedOrder === 'personal' ? 'personal' : 'listed',
    showRecentCategories: typeof f.showRecentCategories === 'boolean' ? f.showRecentCategories : true,
    panel: f.panel === 'none' ? 'none' : 'suggested',
  };
}

export function cleanGoal(v: unknown): ChannelGoal | null {
  if (!isRecord(v)) return null;
  const target = intIn(v.target, 1, GOAL_TARGET_MAX);
  if (target === null) return null;
  return { kind: 'followers', target, title: cleanText(v.title, GOAL_TITLE_MAX) };
}

export function defaultRaids(): RaidSettings {
  return { accept: 'anyone', minViewers: 0, maxViewers: 0, minChannelAgeDays: 0 };
}

export function cleanRaids(v: unknown): RaidSettings {
  const r = isRecord(v) ? v : {};
  const accept = r.accept === 'following' || r.accept === 'nobody' ? r.accept : 'anyone';
  return {
    accept,
    minViewers: intIn(r.minViewers, 0, 100_000_000) ?? 0,
    maxViewers: intIn(r.maxViewers, 0, 100_000_000) ?? 0,
    minChannelAgeDays: intIn(r.minChannelAgeDays, 0, 36_500) ?? 0,
  };
}

export function defaultClips(): ClipSettings {
  return {
    enabled: true,
    seconds: 30,
    whoCanClip: 'anyone',
    captions: true,
    reactions: true,
    comments: true,
    minFollowMinutes: 0,
    excludedCategories: [],
    commentsWho: 'anyone',
    commentsMinFollowMinutes: 0,
    reactionEmojis: [...DEFAULT_CLIP_REACTION_EMOJIS],
  };
}

/** One emoji grapheme (the control plane's rule): a pictograph, optionally with a variation selector or ZWJ-joined pictographs. */
const EMOJI_PATTERN = /^\p{Extended_Pictographic}(\u{FE0F}|\u{200D}\p{Extended_Pictographic})*$/u;
const EMOJI_MAX_LENGTH = 8;
const CATEGORY_SLUG_MAX = 100;

export function isSingleEmoji(v: string): boolean {
  return v.length <= EMOJI_MAX_LENGTH && EMOJI_PATTERN.test(v);
}

/** A follow age from FOLLOW_AGE_MINUTES; anything else is 0 (any follower). */
export function cleanFollowMinutes(v: unknown): number {
  return (FOLLOW_AGE_MINUTES as readonly number[]).includes(v as number) ? (v as number) : 0;
}

/** Valid, unique category slugs, at most CLIP_EXCLUDED_CATEGORIES_MAX. */
export function cleanCategorySlugs(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  const out: string[] = [];
  for (const raw of v) {
    if (typeof raw !== 'string') continue;
    const slug = raw.trim().toLowerCase();
    if (slug.length > CATEGORY_SLUG_MAX || !CATEGORY_SLUG_PATTERN.test(slug) || out.includes(slug)) continue;
    out.push(slug);
    if (out.length >= CLIP_EXCLUDED_CATEGORIES_MAX) break;
  }
  return out;
}

/** Unique single emoji, at most CLIP_REACTION_EMOJIS_MAX; the default set when none is valid. */
export function cleanReactionEmojis(v: unknown): string[] {
  if (!Array.isArray(v)) return [...DEFAULT_CLIP_REACTION_EMOJIS];
  const out: string[] = [];
  for (const raw of v) {
    if (typeof raw !== 'string') continue;
    const emoji = raw.trim();
    if (!isSingleEmoji(emoji) || out.includes(emoji)) continue;
    out.push(emoji);
    if (out.length >= CLIP_REACTION_EMOJIS_MAX) break;
  }
  return out.length > 0 ? out : [...DEFAULT_CLIP_REACTION_EMOJIS];
}

export function cleanClips(v: unknown): ClipSettings {
  const c = isRecord(v) ? v : {};
  const seconds = (CLIP_SECONDS as readonly number[]).includes(c.seconds as number) ? (c.seconds as ClipSeconds) : 30;
  const flag = (x: unknown): boolean => (typeof x === 'boolean' ? x : true);
  return {
    enabled: flag(c.enabled),
    seconds,
    whoCanClip: c.whoCanClip === 'following' ? 'following' : 'anyone',
    captions: flag(c.captions),
    reactions: flag(c.reactions),
    comments: flag(c.comments),
    minFollowMinutes: cleanFollowMinutes(c.minFollowMinutes),
    excludedCategories: cleanCategorySlugs(c.excludedCategories),
    commentsWho: c.commentsWho === 'following' ? 'following' : 'anyone',
    commentsMinFollowMinutes: cleanFollowMinutes(c.commentsMinFollowMinutes),
    reactionEmojis: cleanReactionEmojis(c.reactionEmojis),
  };
}

export function cleanRetentionDays(v: unknown): number {
  return intIn(v, 0, VOD_RETENTION_DAYS_MAX) ?? 0;
}

/** Entries with a name, case-insensitively unique by name (first wins), at most FAVOURITES_MAX. */
export function cleanFavourites(v: unknown): ChannelFavourite[] {
  if (!Array.isArray(v)) return [];
  const seen = new Set<string>();
  const out: ChannelFavourite[] = [];
  for (const raw of v) {
    if (!isRecord(raw)) continue;
    const name = cleanText(raw.name, FAVOURITE_NAME_MAX);
    if (!name || seen.has(name.toLowerCase())) continue;
    seen.add(name.toLowerCase());
    out.push({ name, note: cleanText(raw.note, FAVOURITE_NOTE_MAX) });
    if (out.length >= FAVOURITES_MAX) break;
  }
  return out;
}
